import { orderBy, toNumber } from 'lodash';
import { SortOptions } from 'src/components/subHeader/SubHeaderWithSorting.interface';
import { DefaultQueryProps } from 'src/hooks';
import { IProduct } from 'src/interfaces';

import { filterProductByKeywords } from '../../listProductsByCategoryPage/modules/ListProductsByCategoryPage.helper';

const formatSortName = (sort: SortOptions) => {
  switch (sort) {
    case SortOptions.ALPHABETICALLY:
      return '*product_name';
    case SortOptions.DATE_CREATED:
      return 'created_at';
    case SortOptions.LAST_MODIFIED:
      return 'modified_at';
    default:
      return '*product_name';
  }
};

/** Parse 1/0, "1"/"0", true/false, "true"/"false" -> boolean */
const parseBool = (v: unknown): boolean => {
  if (typeof v === 'boolean') return v;
  if (typeof v === 'number') return v === 1;
  if (typeof v === 'string') {
    const s = v.trim().toLowerCase();
    if (s === 'true') return true;
    if (s === 'false') return false;
    const n = Number(s);
    if (!Number.isNaN(n)) return n === 1;
  }

  return false;
};

/** Convert query value to list; splits "a|b|c" strings */
const toList = (raw: unknown): Array<string | number | boolean> => {
  if (Array.isArray(raw)) return raw;
  if (typeof raw === 'string') return raw.split('|');

  return [raw as any];
};

/** Decide if a product is assigned to any component */
const isAssignedToComponent = (p: IProduct): boolean => {
  const any = (keys: string[]) => {
    for (const k of keys) {
      const v: any = (p as any)[k];
      if (typeof v === 'boolean') return v;
      if (typeof v === 'number') return v > 0 || v === 1;
      if (typeof v === 'string') return parseBool(v);
    }

    return undefined;
  };

  const flag = any([
    'has_assigned_component',
    'has_component',
    'has_assigned',
    'is_assigned',
    'assigned_to_component',
  ]);
  if (typeof flag === 'boolean') return flag;

  const cntRaw: any =
    (p as any).component_count ??
    (p as any).components_count ??
    (p as any).assigned_component_count;
  if (typeof cntRaw === 'number') return cntRaw > 0;
  if (typeof cntRaw === 'string') {
    const n = Number(cntRaw);
    if (!Number.isNaN(n)) return n > 0;
  }

  const arr: any =
    (p as any).components ??
    (p as any).assigned_components ??
    (p as any).component_list;
  if (Array.isArray(arr)) return arr.length > 0;

  return false;
};

export const filteredProducts = (
  products: IProduct[],
  activeFilters: DefaultQueryProps,
  sort: SortOptions,
  order: 'desc' | 'asc',
) => {
  let filteredProducts = products;

  // Keyword (name.regexp)
  if ('name.regexp' in activeFilters) {
    filteredProducts = products.filter((product) => {
      const values = activeFilters['name.regexp'];

      return filterProductByKeywords(values, product);
    });
  }

  // Status
  if ('status.list' in activeFilters) {
    filteredProducts = filteredProducts.filter((product) => {
      const values = activeFilters['status.list'];

      return values.some(
        (value) => Boolean(toNumber(value)) === (product as any).active,
      );
    });
  }

  // Variation type
  if ('product_variation_size_id.list' in activeFilters) {
    filteredProducts = filteredProducts.filter((product) => {
      const values = activeFilters['product_variation_size_id.list'];

      return values.some((value) =>
        (product as any).productVariations?.some(
          (item: any) => item.product_variation_size_id === toNumber(value),
        ),
      );
    });
  }

  // Category
  if ('product_category_id' in activeFilters) {
    filteredProducts = filteredProducts.filter((product) => {
      const values = activeFilters['product_category_id'];
      const pid: any = (product as any).product_category_id;
      if (Array.isArray(pid)) {
        return values.some((value) => pid.includes(toNumber(value)));
      }

      return values.some((value) => Number(pid) === toNumber(value));
    });
  }

  // Assigned-to-component filter (supports ?has_assigned_component=0|1 and .list)
  const assignedKey =
    'has_assigned_component.list' in activeFilters
      ? 'has_assigned_component.list'
      : 'has_assigned_component' in activeFilters
      ? 'has_assigned_component'
      : null;

  if (assignedKey) {
    const raw = (activeFilters as any)[assignedKey];
    const list = toList(raw);
    const targets = new Set(list.map((v) => parseBool(v)));

    filteredProducts = filteredProducts.filter((p) =>
      targets.has(isAssignedToComponent(p)),
    );
  }

  return orderBy(filteredProducts, formatSortName(sort), order);
};
